import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { Paciente } from '../types';
import { PacienteService } from '../services/pacienteService';
import { ConsultasService } from '../services/consultasService';

interface UsePacientesReturn {
  pacientes: Paciente[];
  totalPages: number;
  totalPacientes: number;
  totalConsultas: number;
  paciente: Paciente | undefined;
  loading: boolean;
  error: string | null;
  get: (page?: number, limit?: number, nome?: string) => Promise<void>;
  getStats: (nome?: string) => Promise<void>;
  deleteById: (id: string) => Promise<void>;
  getById: (id: string) => Promise<void>;
  upsert: (body: Partial<Paciente>) => Promise<Paciente | undefined>;
  clear: () => Promise<void>;
}

const usePacientes = (): UsePacientesReturn => {
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPacientes, setTotalPacientes] = useState(0);
  const [totalConsultas, setTotalConsultas] = useState(0);
  const [paciente, setPaciente] = useState<Paciente | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const get = useCallback(
    async (page = 0, limit = 10, nome?: string): Promise<void> => {
      if (!currentOrganization) return;

      setLoading(true);
      setError(null);

      try {
        const response = await PacienteService.getPacientes(
          currentOrganization.id,
          page,
          limit,
          nome
        );

        if (response.success && response.data) {
          setPacientes(response.data);
          setTotalPages(Math.ceil((response.count || 0) / limit));
          setTotalPacientes(response.count || 0);
        } else {
          setError(response.message);
          if (response.error?.message.includes('401')) {
            navigate('/login');
          }
        }
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [navigate, currentOrganization]
  );

  const getStats = useCallback(
    async (nome?: string): Promise<void> => {
      if (!currentOrganization) return;

      try {
        // Get consultas count for filtered pacientes
        const response = await ConsultasService.getConsultasCountForPacientes(
          currentOrganization.id,
          nome
        );

        if (response.success) {
          setTotalConsultas(response.count || 0);
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        // Don't set error state for stats, just log it
        console.error('Error fetching consultas stats:', error);
      }
    },
    [currentOrganization]
  );

  const getById = useCallback(
    async (id: string): Promise<void> => {
      if (!currentOrganization) return;

      setLoading(true);
      setError(null);

      try {
        const response = await PacienteService.getPacienteById(id, currentOrganization.id);

        if (response.success && response.data) {
          setPaciente(response.data);
        } else {
          setError(response.message);
          if (response.error?.message.includes('401')) {
            navigate('/login');
          }
        }
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [navigate, currentOrganization]
  );

  const deleteById = useCallback(
    async (id: string): Promise<void> => {
      if (!currentOrganization) return;

      setLoading(true);
      setError(null);

      try {
        const response = await PacienteService.deletePaciente(id, currentOrganization.id);

        if (!response.success) {
          setError(response.message);
          if (response.error?.message.includes('401')) {
            navigate('/login');
          }
        }
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [navigate, currentOrganization]
  );

  const upsert = useCallback(
    async (body: Partial<Paciente>): Promise<Paciente | undefined> => {
      if (!currentOrganization) return;

      setError(null);

      try {
        const response = await PacienteService.upsertPaciente(currentOrganization.id, body);

        if (response.success && response.data) {
          setPaciente(response.data);
          return response.data;
        } else {
          setError(response.message);
          if (response.error?.message.includes('401')) {
            navigate('/login');
          }
        }
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [navigate, currentOrganization]
  );

  const clear = useCallback(async (): Promise<void> => {
    setPacientes([]);
    setPaciente(undefined);
  }, []);

  return {
    pacientes,
    totalPages,
    totalPacientes,
    totalConsultas,
    paciente,
    loading,
    error,
    get,
    getStats,
    deleteById,
    getById,
    upsert,
    clear
  };
};

export default usePacientes;
